import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import {
  utils as xlsxUtils,
  writeFile
} from 'xlsx';
import { colors } from '@constants/colors';
import { ganttBarIconButtonStyles } from '@constants/mui-theme';
import ExcelIcon from '@icons/excel-icon';
import { Icon, IconButton } from '@mui';
import FixedSeparator from '@shared/helpers/fixed-separator';
import FlexFiller from '@shared/helpers/flex-filler';
import UnstyledHeading from '@shared/unstyled-heading';
import { buildExportRows, buildExcelFilename, ganttToArray } from '@utils/excel-utils';
import EntityFilter from './entity-filter';
import DateFilter from './date-filter';
import LocationFilter from './location-filter';
import './top-bar.scss';

const TopBar = () => {
  const dispatch = useDispatch();

  const agencyTypes = useSelector(state => state.dataTypes.agency_type);
  const edit = useSelector(state => state.groups.edit);
  const group = useSelector(state => state.groups.edit.group);
  const ganttFilters = useSelector(state => state.groups.gantt.filters);

  const { entity, locationType } = ganttFilters;
  const exportRows = buildExportRows(entity, locationType, edit);

  const onBackClick = useCallback(() => {
    const { id, type_name } = group;
    const path = `/group/${type_name}/${id}`;
    dispatch(push(path));
  }, [dispatch, group]);

  const exportToExcel = useCallback(() => {
    const data = ganttToArray(agencyTypes, group, exportRows);
    const ws = xlsxUtils.aoa_to_sheet(data);
    const wb = xlsxUtils.book_new();
    xlsxUtils.book_append_sheet(wb, ws, 'SheetJS');
    const outputFilename = buildExcelFilename(locationType, group.id);
    writeFile(wb, outputFilename);
  }, [agencyTypes, exportRows, group, locationType]);

  if (typeof locationType === 'undefined') {
    // If the locationType is undefined, we still didn't loaded the config,
    // wait until it's available to render the top bar, else dropdowns
    // will be initialized with no data.
    return null;
  }

  const separatorStyle = {
    minWidth: '1rem',
    width: '1rem'
  };
  const lastSeparatorStyle = {
    borderRight: '1px solid rgba(0,0,0,0.1)',
    height: '100%',
    minWidth: '1.5rem',
    width: '1.5rem'
  };

  return (
    <div styleName="gantt-top-bar">
      <IconButton onClick={onBackClick} aria-label="Back">
        <Icon color={colors.neutral.white} aria-hidden="true">arrow_back</Icon>
      </IconButton>
      <UnstyledHeading rank={1}>Gantt chart of coordination group {group.name}</UnstyledHeading>
      <FlexFiller />
      <EntityFilter />
      <FixedSeparator style={separatorStyle} />
      <LocationFilter />
      <FixedSeparator style={separatorStyle} />
      <DateFilter />
      <FixedSeparator style={lastSeparatorStyle} />
      <IconButton
        aria-label="Export as xls"
        onClick={exportToExcel}
        style={ganttBarIconButtonStyles}
        tooltip="Export as xls"
      >
        <ExcelIcon />
      </IconButton>
    </div>
  );
};

export default TopBar;
